import React, { useState, useMemo } from 'react'
import {
  BillingSectionTitle,
  BillingStyled,
  LastThreeInvoices,
  BillingSectionTitleRow,
  BillingSectionEdit,
  BillingWireTransfer,
  BillingWireTransferTitle,
  BillingWireTransferStatus,
  RectangleStatus,
} from 'Features/accountSettings/components/Billing/Billing.styled'
import { useTranslation } from 'react-i18next'
import { InvoicesTable } from 'Features/accountSettings/components/Billing/InvoicesTable/InvoicesTable'
import { useGetAccountSettings } from 'Features/accountSettings/api/getBilling'
import { ClickCeaseCreditCard } from 'Features/accountSettings/components/Billing/ClickCeaeCreditCard/ClickCeaseCreditCard'
import { EditBillingDetails } from 'Features/accountSettings/components/Billing/EditBillingDetailsModal/EditBillingDetailsModal'
import { InvoicesTableModal } from 'Features/accountSettings/components/Billing/InvoicesModal/InvoicesModal'
import { PaymentMethodModal } from 'Features/accountSettings/components/Billing/PaymentMethodModal/PaymentMethodModal'
import { BillingDetails } from 'Features/accountSettings/components/Billing/BillingDetails'
import { useGetInvoices } from 'Features/accountSettings/api/getInvoices'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleCheck, faClock } from '@fortawesome/pro-light-svg-icons'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { getAmountWithCurrency } from 'Utils/currencyHelper'
import { fromUnixTime, isAfter } from 'date-fns'
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons'
import { PAYMENT_METHOD } from 'Types/PaymentMethod'
import { getFormattedDate } from 'Utils/dates'

export const Billing = ({
  openPaymentModal,
  flowOrigin,
}: {
  openPaymentModal: boolean
  flowOrigin: string
}) => {
  const { t } = useTranslation('accountSettings')

  const [showEditBillingModal, setShowEditBillingModal] = useState<boolean>(false)
  const [showInvoicesModal, setShowInvoicesModal] = useState<boolean>(false)
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState<boolean>(
    openPaymentModal || false
  )
  const [accountSettings, creditCard] = useGetAccountSettings()
  const invoices = useGetInvoices()
  const accounts = useMemo(() => {
    if (!(accountSettings.data instanceof Error)) {
      return accountSettings.data
    }
  }, [accountSettings])

  const card = useMemo(() => {
    if (!(creditCard.data instanceof Error)) {
      return creditCard.data
    }
  }, [creditCard])

  // get the latest wire transfer invoice (if exists)
  const latestInvoice = invoices?.data?.at(0)
  const lastWireTransferInvoice =
    latestInvoice?.paymentMethod === PAYMENT_METHOD.WIRE_TRANSFER ? latestInvoice : undefined

  const amountWithCurrency = lastWireTransferInvoice
    ? getAmountWithCurrency(lastWireTransferInvoice?.currency, lastWireTransferInvoice?.amount)
    : ''

  const amountPayOrDue =
    lastWireTransferInvoice && lastWireTransferInvoice.status === 'Succeeded'
      ? t('billing_page.amount_paid', { amount: amountWithCurrency })
      : lastWireTransferInvoice && lastWireTransferInvoice.status === 'Pending'
      ? t('billing_page.amount_due', { amount: amountWithCurrency })
      : ''

  const payOrDueDate = (() => {
    if (lastWireTransferInvoice?.status === 'Succeeded') {
      const invoiceDate = fromUnixTime(lastWireTransferInvoice?.date)
      return t('billing_page.date_received', {
        date: getFormattedDate(invoiceDate),
      })
    } else if (lastWireTransferInvoice?.dueDate) {
      const dueDate = fromUnixTime(lastWireTransferInvoice.dueDate)
      return t('billing_page.payment_due_date', {
        date: getFormattedDate(dueDate),
      })
    }
    return ''
  })()

  const currentDate = new Date()
  const currentTimestamp = Math.floor(currentDate.getTime() / 1000)
  const wireTransferNotPaid =
    lastWireTransferInvoice &&
    lastWireTransferInvoice.status === 'Pending' &&
    isAfter(currentTimestamp, lastWireTransferInvoice?.dueDate)

  return (
    <BillingStyled id={'account-settings-billing-tab'}>
      <BillingSectionTitleRow>
        <BillingSectionTitle noCard={(!card || !card.cardLast4Digits) && !lastWireTransferInvoice}>
          {t('billing_page.payment_method')}
        </BillingSectionTitle>
        {!lastWireTransferInvoice && (
          <BillingSectionEdit
            onClick={() => {
              setShowPaymentMethodModal(true)
            }}
          >
            {t('billing_page.edit')}
          </BillingSectionEdit>
        )}
      </BillingSectionTitleRow>
      {lastWireTransferInvoice && (
        <>
          <BillingWireTransferTitle>
            {t('billing_page.wire_transfer_payment')}
          </BillingWireTransferTitle>
          <BillingWireTransfer>{amountPayOrDue}</BillingWireTransfer>
          <BillingWireTransfer>{payOrDueDate}</BillingWireTransfer>
          <BillingWireTransfer>
            {t('billing_page.status')}:
            <BillingWireTransferStatus
              css={{
                color:
                  lastWireTransferInvoice.status === 'Succeeded'
                    ? '$success'
                    : wireTransferNotPaid
                    ? '$accent'
                    : '$info',
              }}
            >
              <RectangleStatus
                css={{
                  backgroundColor:
                    lastWireTransferInvoice.status === 'Succeeded'
                      ? '$lightGreen'
                      : wireTransferNotPaid
                      ? '$lightGold'
                      : '$lightBlue',
                }}
              >
                <FontAwesomeIcon
                  style={{ paddingInline: '4px', marginTop: '3px', width: '10px' }}
                  icon={
                    lastWireTransferInvoice.status === 'Succeeded'
                      ? (faCircleCheck as IconProp)
                      : wireTransferNotPaid
                      ? (faExclamationCircle as IconProp)
                      : (faClock as IconProp)
                  }
                />
                {lastWireTransferInvoice.status === 'Succeeded'
                  ? t('billing_page.wire_received')
                  : wireTransferNotPaid
                  ? t('billing_page.wire_not_paid')
                  : t('billing_page.wire_pending')}
              </RectangleStatus>
            </BillingWireTransferStatus>
          </BillingWireTransfer>
        </>
      )}
      {card && card.cardLast4Digits && (
        <ClickCeaseCreditCard
          cardNumber={`****   ****   ****   ${card.cardLast4Digits}`}
          validThru={`${card.cardExpirationMonth}/${card.cardExpirationYear}`}
        />
      )}
      <LastThreeInvoices>
        <BillingSectionTitleRow>
          <BillingSectionTitle>{t('billing_page.last_three_invoices')}</BillingSectionTitle>
          <BillingSectionEdit
            onClick={() => {
              setShowInvoicesModal(true)
            }}
          >
            {t('billing_page.view_all')}
          </BillingSectionEdit>
        </BillingSectionTitleRow>
        <InvoicesTable numOfRows={3} />
      </LastThreeInvoices>

      <BillingDetails
        accounts={accounts}
        openEditBillingModal={() => setShowEditBillingModal(true)}
      />

      {showEditBillingModal && (
        <EditBillingDetails
          isOpen={showEditBillingModal}
          onOpenChange={() => setShowEditBillingModal(false)}
        />
      )}
      <InvoicesTableModal
        isOpen={showInvoicesModal}
        onOpenChange={() => setShowInvoicesModal(false)}
      />
      <PaymentMethodModal
        openPaymentMethod={openPaymentModal}
        origin={flowOrigin}
        isOpen={showPaymentMethodModal}
        onOpenChange={() => setShowPaymentMethodModal(false)}
      />
    </BillingStyled>
  )
}
